import React, { useState, useEffect } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import { useNavigate, useLocation, matchPath } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

// material-ui
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

import Box from "@mui/material/Box";

// project import
import Dot from "components/@extended/Dot";

import { MenuOrientation, ThemeMode } from "config";
import useConfig from "hooks/useConfig";
import { handlerDrawerOpen, useGetMenuMaster } from "api/menu";
import { getPermissionCheckData } from "utils/getHierarchy";
import { getCountryList } from "utils/timezone";
import { ReactComponent as ProIcon } from "assets/images/icons/icn-pro.svg";

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const wiFiPlannerDomainRules = [
  {
    /**
     * Regex Breakdown:
     * ^unity         -> Starts with 'unity'
     * (?:-(rd\d?|qa\d?|[a-z]{2}))? -> Optional: dash followed by 'rd/qa + number' OR any 2-letter country code
     * \.nuclias\.com$ -> Ends with '.nuclias.com'
     */
    match: /^unity(?:-(rd\d?|qa\d?|[a-z]{2}))?\.nuclias\.com$/,
    LogoutRidirect: (domain) => `https://${domain}`,
    url: (domain) => `https://wfp.${domain}/Portaldlink?RAS=&FM=`,
  },
  {
    // Localhost remains static for development
    match: (domain) => domain === "localhost",
    LogoutRidirect: "https://unity-rd3.nuclias.com",
    url: "https://wfp.unity-rd3.nuclias.com/Portaldlink?RAS=&FM=",
  },
];

function getWiFiPlannerConfig(domain) {
  const rule = wiFiPlannerDomainRules.find(({ match }) => {
    if (match instanceof RegExp) return match.test(domain);
    if (typeof match === "function") return match(domain);
    return false;
  });

  // Helper to execute function if dynamic, or return string if static
  const resolveValue = (val, input) =>
    typeof val === "function" ? val(input) : val;

  return {
    url: rule ? resolveValue(rule.url, domain) : "/site/dashboard",
    LogoutRidirect: rule ? resolveValue(rule.LogoutRidirect, domain) : null,
  };
}

export default function NavItem({
  item,
  level,
  isParents = false,
  setSelectedID,
}) {
  const theme = useTheme();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const navigate = useNavigate();
  const ws = useWebSocket();
  const { token } = useAuth();
  const domain = window.location.hostname;
  const downLG = useMediaQuery(theme.breakpoints.down("lg"));

  const { mode, menuOrientation } = useConfig();
  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  const isPageItem = [
    "dashboard",
    "aInsight",
    "topology",
    "floor-plan-and-heat-map",
    "client",
    "summary-report",
    "help",
    "privacy-policy",
    "logout",
    "orgs-scope",
    "users",
    "settings",
    "site-scope",
    "devices",
    "msp-devices",
    "wifiPlanner",
    "events",
    "msp-events",
    "firmware-managements",
    "clients",
    "support",
    "license-management",
    "msp-license-management",
    "redeem_delegated_access",
    "surveillance-dashboard",
    "surveillance-devices",
    "surveillance-liveview",
    "surveillance-playback",
  ].includes(item.id);

  const [readSiteGeneralSettingUuid, _setReadSiteGeneralSettingUuid] =
    useState(uuidv4());

  const [wifiPlannerProcess, setWifiPlannerProcess] = useState("");
  const [wifiPlannerRequest, setWifiPlannerRequest] = useState({
    OBU: "DHQ", // 固定 DHQ
    Country: "", // Country: Organization(SiteGeneralSetting) 的 Country
    UserType: "D-Link", // 固定 D-Link
    CompanyUID: "DK002", // 固定 DK002
    CompanyName: "D-Link", // 固定 D-Link
    Email: "", // Site 的 id
    FirstName: "", // Organization 的 name
    LastName: "", // Site 的 name
    JobTitle: "",
    ToolsFunction: "",
    ProductModule: "",
    Privileges: "1",
    Project: "10", // 固定 10
    Floorplan: "50", // 固定 50
    OrgID: "", // Organization 的 id
    OauthToken: token, // token
    LogoutRidirect: "", // domain，https://unity-rd.nuclias.com, https://unity-qa.nuclias.com ...etc.
    ExtParameter1: "",
    ExtParameter2: "",
  });

  // 因為父層想呼叫子層的 hook (useDataSourceManager) 呼叫不到
  // 所以無法使用 sendRequest 和 dataMap
  // 所以用此方法

  function fetchSiteGeneralSettingData(resourceUrl) {
    const requestSiteGeneralSetting = {
      jsonrpc: "2.0",
      id: readSiteGeneralSettingUuid,
      method: "read",
      params: {
        resource: resourceUrl,
        accessToken: token,
      },
    };
    ws.send(JSON.stringify(requestSiteGeneralSetting));
  }

  function handleSiteGeneralSettingData(msg) {
    if (msg.id !== readSiteGeneralSettingUuid) return;
    const countryList = getCountryList();
    const currentCountry = countryList.find(
      (item) => item.value === msg.result.data.countryDomain.countryIndex,
    );
    setWifiPlannerRequest((pre) => ({ ...pre, Country: currentCountry.label }));
    setWifiPlannerProcess("navigateWifiPlanner");
  }

  const itemHandler = async () => {
    handlerDrawerOpen(false);

    if (item.id === "wifiPlanner") {
      const permissionCheckData = await getPermissionCheckData({ ws, token });

      // 目前只判斷 Standalone 的部分
      if (
        !permissionCheckData.validatePermission.realm ||
        !permissionCheckData.validatePermission.site
      )
        navigate("/login");

      setWifiPlannerProcess("fetchSiteGeneralSettingData");
      const { LogoutRidirect } = getWiFiPlannerConfig(domain);
      const currentSelectBreadcrumbs = JSON.parse(
        localStorage.getItem("selectBreadcrumbs") || "{}",
      );

      const standalone =
        !currentSelectBreadcrumbs.orgs.id ||
        Object.keys(currentSelectBreadcrumbs.orgs).length === 0;

      const restfulParamsConfig = {
        standalone: {
          orgId: currentSelectBreadcrumbs.realm?.id,
          orgName: currentSelectBreadcrumbs.realm?.name,
          siteId: currentSelectBreadcrumbs.site?.id,
          siteName: currentSelectBreadcrumbs.site?.name,
        },
        nonStandalone: {
          entId: currentSelectBreadcrumbs.realm?.id,
          orgId: currentSelectBreadcrumbs.orgs?.id,
          orgName: currentSelectBreadcrumbs.orgs?.name,
          siteId: currentSelectBreadcrumbs.site?.id,
          siteName: currentSelectBreadcrumbs.site?.name,
        },
      };

      const mode = standalone ? "standalone" : "nonStandalone";
      const restfulParams = restfulParamsConfig[mode];

      setWifiPlannerRequest((pre) => ({
        ...pre,
        Email: restfulParams.siteId,
        FirstName: restfulParams.orgName,
        LastName: restfulParams.siteName,
        LogoutRidirect: LogoutRidirect,
        OrgID: restfulParams.orgId,
      }));

      const resourceUrl = `${
        standalone ? "" : `/ents/${restfulParams.entId}`
      }/orgs/${restfulParams.orgId}/sites/${
        restfulParams.siteId
      }/settings/general`;
      fetchSiteGeneralSettingData(resourceUrl);
      return;
    }

    /*  if (item.id === "resource-center") {
      window.open(
        "https://www.dlink.com/en/for-business/nuclias/nuclias-unity-Resource-Center",
        "_blank",
      );
      return;
    } */

    if (isParents && setSelectedID) {
      setSelectedID();
    }

    navigate(item.url);
  };

  useEffect(() => {
    if (!ws) return;

    const handleMessage = (message) => {
      const msg = JSON.parse(message.data);
      handleSiteGeneralSettingData(msg);
    };

    ws.addEventListener("message", handleMessage);

    return () => {
      ws.removeEventListener("message", handleMessage);
    };
  }, [ws]);

  useEffect(() => {
    if (wifiPlannerProcess === "navigateWifiPlanner") {
      const { url } = getWiFiPlannerConfig(domain);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = url;
      form.target = "_blank";

      Object.keys(wifiPlannerRequest).forEach((key) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = wifiPlannerRequest[key];
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
      setWifiPlannerProcess("");
    }
  }, [wifiPlannerProcess]);

  const { pathname } = useLocation();
  const isSelected = !!matchPath(
    { path: item?.link ? item.link : item.url, end: false },
    pathname,
  );
  const textColor = mode === ThemeMode.DARK ? "grey.400" : "text.primary";
  const iconSelectedColor =
    mode === ThemeMode.DARK && drawerOpen ? "text.primary" : "primary.main";
  const pageIconColor = isPageItem
    ? isSelected
      ? "#172664"
      : "#ffffff"
    : isSelected
      ? "#172664"
      : textColor;

  const Icon = isSelected ? item.iconSelect : item.icon;
  const itemIcon = Icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? "1rem" : "1.25rem",
        ...(menuOrientation === MenuOrientation.HORIZONTAL &&
          isParents && { fontSize: 20, stroke: "1.5" }),
        color: pageIconColor,
      }}
    />
  ) : (
    false
  );

  return (
    <>
      {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
        <Box sx={{ position: "relative" }}>
          <ListItemButton
            component='div'
            target={itemTarget}
            disabled={item.disabled}
            selected={isSelected}
            sx={{
              zIndex: 1201,
              pl: drawerOpen ? `${level * 18}px` : 1.5,
              py: !drawerOpen && level === 1 ? 0.6 : 0.6,
              position: "relative",
              overflow: "hidden",

              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                height: "0",
                width: "0",
                backgroundColor: "#8695cb",
                borderRadius: "30px",
                zIndex: -1,
              },
              ...(drawerOpen &&
                isPageItem && {
                  "& .pro-icon path": {
                    fill: "#ffffff",
                  },
                  "&:hover": {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  },
                  "&:hover::before": {
                    height: "44px",
                    width: "234px",
                    left: "calc(50% + 18px)",
                  },
                  "&:hover .pro-icon path": {
                    fill: "#172664",
                  },
                  "&.Mui-selected::before": {
                    backgroundColor: "#ffffff",
                    height: "44px",
                    width: "270px",
                  },
                  "&.Mui-selected .pro-icon path": {
                    fill: "#172664",
                  },
                  "&.Mui-selected:hover::before": {
                    backgroundColor: "#ffffff",
                    height: "44px",
                    width: "270px",
                    left: "50%",
                  },
                  "&.Mui-selected": {
                    color: "#172664",
                  },
                }),
              ...(!drawerOpen && {
                "&:hover": { bgcolor: "transparent" },
                "&.Mui-selected": {
                  "&:hover": { bgcolor: "transparent" },
                  bgcolor: "transparent",
                },
              }),
              // collapse item
              ...(drawerOpen &&
                !isPageItem && {
                  borderRadius: "5px",
                  paddingLeft: "14px",
                  "&:hover": { bgcolor: "#e8ebfc" },
                  "&.Mui-selected": {
                    "&:hover": { bgcolor: "#dbe0fc" },
                    bgcolor: "#dbe0fc",
                  },
                }),
              ...(!drawerOpen &&
                !isPageItem && {
                  borderRadius: "5px",
                  paddingLeft: "14px",
                  "&:hover": { bgcolor: "#e8ebfc" },
                  "&.Mui-selected": {
                    "&:hover": { bgcolor: "#dbe0fc" },
                    bgcolor: "#dbe0fc",
                  },
                }),
            }}
            onClick={() => {
              itemHandler();
            }}
          >
            {itemIcon &&
              (drawerOpen === false ? (
                <Tooltip title={item.title} placement='right'>
                  <ListItemIcon
                    sx={{
                      minWidth: 28,
                      color: isSelected ? iconSelectedColor : textColor,
                      borderRadius: 30,
                      width: 44,
                      height: 44,
                      alignItems: "center",
                      justifyContent: "center",
                      ...(isSelected && {
                        bgcolor: "#ffffff",
                      }),
                    }}
                  >
                    {itemIcon}
                  </ListItemIcon>
                </Tooltip>
              ) : (
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: isSelected ? iconSelectedColor : textColor,
                  }}
                >
                  {itemIcon}
                </ListItemIcon>
              ))}

            {(drawerOpen || (!drawerOpen && level !== 1)) && (
              <ListItemText
                primary={
                  <Typography
                    sx={{
                      color: isPageItem
                        ? isSelected
                          ? "#172664"
                          : "#ffffff"
                        : "#172664",
                      fontSize: isPageItem ? "15px" : "14px",
                      fontWeight: isPageItem ? "bold" : "normal",
                    }}
                  >
                    <>
                      {item.title}
                      {item.pro && (
                        <ProIcon
                          aria-label='Pro'
                          className='pro-icon'
                          style={{
                            display: "inline-block",
                            verticalAlign: "middle",
                            marginLeft: 8,
                          }}
                        />
                      )}
                    </>
                  </Typography>
                }
              />
            )}
          </ListItemButton>
        </Box>
      ) : (
        <ListItemButton
          component='div'
          target={itemTarget}
          disabled={item.disabled}
          selected={isSelected}
          onClick={() => itemHandler()}
          sx={{
            zIndex: 1201,
            ...(isParents && { p: 1, mr: 1 }),
          }}
        >
          {itemIcon && (
            <ListItemIcon
              sx={{
                minWidth: 28,
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  width: 28,
                  height: 28,
                  alignItems: "center",
                  justifyContent: "flex-start",
                  "&:hover": { bgcolor: "transparent" },
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: "transparent",
                    "&:hover": { bgcolor: "transparent" },
                  }),
              }}
            >
              {itemIcon}
            </ListItemIcon>
          )}

          {!itemIcon && (
            <ListItemIcon
              sx={{
                color: isSelected ? "primary.main" : "secondary.dark",
                ...(!drawerOpen && {
                  borderRadius: 1.5,
                  alignItems: "center",
                  justifyContent: "flex-start",
                  "&:hover": { bgcolor: "transparent" },
                }),
                ...(!drawerOpen &&
                  isSelected && {
                    bgcolor: "transparent",
                    "&:hover": { bgcolor: "transparent" },
                  }),
              }}
            >
              <Dot size={4} color={isSelected ? "primary" : "secondary"} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={
              <Typography
                variant='h6'
                color={isSelected ? "primary.main" : "secondary.dark"}
              >
                {item.title}
              </Typography>
            }
          />
        </ListItemButton>
      )}
    </>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.any,
};
